<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'


const props = defineProps({
  estimation:           { type: Object,  default: () => ({}) },
  projet:               { type: Object,  default: () => ({}) },
  type_parties:         { type: Array,   default: () => [] },
  show_prenom:          { type: Boolean, default: false },
  show_type_fonction:   { type: Boolean, default: false },
  show_type_equipement: { type: Boolean, default: false },
  inventaire_enabled:   { type: Boolean, default: false },
  show_type_taux:       { type: Boolean, default: false },
  can_see_costs:        { type: Boolean, default: false },
  can_update:           { type: Boolean, default: false },
  can_delete:           { type: Boolean, default: false },
  projet_url:           { type: String,  default: '/projets' },
  edit_url:             { type: String,  default: '#' },
  destroy_url:          { type: String,  default: '#' },
  xlsx_url:             { type: String,  default: '#' },
  new_rapport_url:      { type: String,  default: '#' },
  edit_personnel_url:           { type: String, default: '#' },
  edit_personnel_detail_url:    { type: String, default: '#' },
  edit_equipements_url:         { type: String, default: '#' },
  edit_equipements_detail_url:  { type: String, default: '#' },
  edit_personnel_externes_url:  { type: String, default: '#' },
  edit_equipement_externes_url: { type: String, default: '#' },
  edit_items_url:               { type: String, default: '#' },
  edit_inventaires_url:         { type: String, default: '#' },
  import_items_url:             { type: String, default: '#' },
  import_personnel_externes_url:  { type: String, default: '#' },
  import_equipement_externes_url: { type: String, default: '#' },
  liste_personnel_externes_url:   { type: String, default: '#' },
  liste_equipement_externes_url:  { type: String, default: '#' },
  can_import_items:              { type: Boolean, default: false },
  can_import_personnel_externes: { type: Boolean, default: false },
  can_import_equipement_externes: { type: Boolean, default: false },
})

// ── Classes ──────────────────────────────────────────────────
const sectionHdrCls   = 'px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between'
const sectionTitleCls = 'text-[11px] font-black uppercase tracking-widest text-outline'
const thCls           = 'px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline'
const tdCls           = 'px-4 py-3 text-sm text-on-surface'
const labelCls        = 'text-[10px] font-black uppercase tracking-widest text-outline/60'

// ── Helpers ───────────────────────────────────────────────────
function fmt(val) {
  if (val == null || val === '' || val === 0) return '—'
  return Number(val).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function deleteEstimation() {
  if (confirm(`Supprimer le bordereau ${props.estimation.code} ? Cette action est irréversible.`)) {
    router.delete(props.destroy_url)
  }
}

function deleteElement(el) {
  if (confirm('Supprimer cet item ?')) {
    router.delete(el.destroy_url)
  }
}

function deleteInventaire(inv) {
  if (confirm('Supprimer cet inventaire ?')) {
    router.delete(inv.destroy_url)
  }
}

// ── Accordéons des cards du haut ─────────────────────────────
const showTagsDetail  = ref(false)
const showUsersDetail = ref(false)

// ── Bouton ↑ scroll ───────────────────────────────────────────
const showScrollTop = ref(false)
let scrollEl = null
function onScroll() { showScrollTop.value = (scrollEl?.scrollTop ?? 0) > 300 }
onMounted(() => {
  scrollEl = document.querySelector('main')
  scrollEl?.addEventListener('scroll', onScroll)
})
onUnmounted(() => scrollEl?.removeEventListener('scroll', onScroll))
function scrollToTop() { document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' }) }

// ── Items groupés par tag ────────────────────────────────────
const elementGroups = computed(() => {
  const groups = []
  const tagMap = {}
  for (const el of (props.estimation.elements ?? [])) {
    const key = el.tag ?? ''
    if (!tagMap[key]) {
      tagMap[key] = { tag: key, elements: [] }
      groups.push(tagMap[key])
    }
    tagMap[key].elements.push(el)
  }
  return groups
})

</script>

<template>
  <AppLayout>
    <!-- Topbar -->
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="projet_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <a :href="projet_url" class="font-mono text-sm font-bold text-primary hover:brightness-125">{{ projet.code }}</a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">{{ estimation.code }}</span>
      </div>
    </template>

    <div class="p-8">
      <div class="space-y-6">

        <!-- En-tête -->
        <div class="flex items-start justify-between gap-4">
          <div>
            <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">{{ estimation.nom }}</h1>
            <p class="text-sm text-outline mt-1">
              Projet <span class="font-mono font-bold text-primary">{{ projet.code }}</span> — {{ projet.nom }}
            </p>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <a :href="projet_url"
               class="flex items-center gap-1.5 px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-sm">arrow_back</span>Retour
            </a>
            <a :href="new_rapport_url"
               class="flex items-center gap-1.5 px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-sm">add</span>Nouveau bordereau
            </a>
            <a :href="xlsx_url"
               class="flex items-center gap-1.5 px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-sm">download</span>XLSX
            </a>
            <a v-if="can_update" :href="edit_url"
               class="flex items-center gap-1.5 px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
            <button v-if="can_delete" type="button" @click="deleteEstimation"
                    class="flex items-center gap-1.5 px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest text-error/70 hover:text-error bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-sm">delete</span>
            </button>
          </div>
        </div>

        <!-- ── Ligne du haut : 3 cards ── -->
        <div class="grid grid-cols-3 gap-6 items-start">

          <!-- Card 1 : Informations générales -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-5 py-3 border-b border-outline-variant/10 bg-surface-container-high/40">
              <h2 :class="sectionTitleCls">Informations générales</h2>
            </div>
            <div class="p-5 space-y-4">
              <div class="grid grid-cols-2 gap-x-4 gap-y-3">
                <div>
                  <p :class="labelCls + ' mb-1'">Code</p>
                  <p class="text-sm font-mono font-bold text-on-surface">{{ estimation.code || '—' }}</p>
                </div>
                <div>
                  <p :class="labelCls + ' mb-1'">Rapports</p>
                  <p class="text-sm text-on-surface">{{ estimation.rapports_count }}</p>
                </div>
                <div>
                  <p :class="labelCls + ' mb-1'">Soumission</p>
                  <p class="text-sm text-on-surface">{{ estimation.date_soumission || '—' }}</p>
                </div>
                <div>
                  <p :class="labelCls + ' mb-1'">Acceptation</p>
                  <p class="text-sm text-on-surface">{{ estimation.date_acceptation || '—' }}</p>
                </div>
              </div>
              <div v-if="estimation.partie_suivies?.length > 0">
                <p :class="labelCls + ' mb-1.5'">Parties suivies</p>
                <div class="flex flex-wrap gap-1.5">
                  <a v-for="ps in estimation.partie_suivies" :key="ps.id"
                     :href="ps.elements_url"
                     class="px-2 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary hover:bg-primary/20 hover:brightness-125 transition-all cursor-pointer">{{ ps.code }}</a>
                </div>
              </div>
              <div v-if="estimation.description">
                <p :class="labelCls + ' mb-1'">Description</p>
                <p class="text-sm text-on-surface leading-relaxed">{{ estimation.description }}</p>
              </div>
            </div>
          </div>

          <!-- Card 2 : Catégories -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-5 py-3 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
              <h2 :class="sectionTitleCls">
                Catégories
                <span class="text-outline/50">({{ estimation.tags?.length ?? 0 }})</span>
              </h2>
              <button v-if="estimation.tags?.length > 0" type="button"
                      @click="showTagsDetail = !showTagsDetail"
                      class="text-[9px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all flex items-center gap-1">
                {{ showTagsDetail ? 'Réduire' : 'Voir plus' }}
                <span class="material-symbols-outlined text-xs">{{ showTagsDetail ? 'expand_less' : 'expand_more' }}</span>
              </button>
            </div>
            <div class="p-5">
              <div v-if="!estimation.tags?.length" class="text-sm text-outline/50 italic">Aucune catégorie</div>
              <div v-else>
                <!-- Liste complète ou tronquée selon état de l'accordéon -->
                <div>
                  <div v-for="(tag, i) in (showTagsDetail ? estimation.tags : estimation.tags.slice(0, 5))"
                       :key="tag.tag"
                       :class="i % 2 === 0 ? 'bg-surface-container-high/50' : ''"
                       class="px-2 py-2 text-sm text-on-surface rounded">
                    {{ tag.tag }}
                  </div>
                  <p v-if="!showTagsDetail && estimation.tags.length > 5"
                     class="text-[10px] text-outline/50 mt-2 px-2">
                    + {{ estimation.tags.length - 5 }} autre(s)
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 3 : Utilisateurs -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-5 py-3 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
              <h2 :class="sectionTitleCls">
                Utilisateurs
                <span class="text-outline/50">({{ estimation.utilisateurs?.length ?? 0 }})</span>
              </h2>
              <button v-if="estimation.utilisateurs?.length > 5" type="button"
                      @click="showUsersDetail = !showUsersDetail"
                      class="text-[9px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all flex items-center gap-1">
                {{ showUsersDetail ? 'Réduire' : 'Voir plus' }}
                <span class="material-symbols-outlined text-xs">{{ showUsersDetail ? 'expand_less' : 'expand_more' }}</span>
              </button>
            </div>
            <div class="p-5">
              <div v-if="!estimation.utilisateurs?.length" class="text-sm text-outline/50 italic">Aucun utilisateur</div>
              <div v-else>
                <div v-for="(u, i) in (showUsersDetail ? estimation.utilisateurs : estimation.utilisateurs.slice(0, 5))"
                     :key="u.nom + i"
                     :class="i % 2 === 0 ? 'bg-surface-container-high/50' : ''"
                     class="px-2 py-2 rounded flex items-center justify-between gap-2">
                  <span class="text-sm text-on-surface">{{ u.prenom }} {{ u.nom }}</span>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <span v-if="u.role" class="text-[10px] font-bold text-outline uppercase tracking-wide">{{ u.role }}</span>
                    <span class="material-symbols-outlined text-sm" :class="u.actif ? 'text-primary' : 'text-outline/30'">
                      {{ u.actif ? 'check_circle' : 'cancel' }}
                    </span>
                  </div>
                </div>
                <p v-if="!showUsersDetail && estimation.utilisateurs.length > 5"
                   class="text-[10px] text-outline/50 mt-2 px-2">
                  + {{ estimation.utilisateurs.length - 5 }} autre(s)
                </p>
              </div>
            </div>
          </div>

        </div><!-- fin grid 3 cols -->

        <!-- ── 4. Personnel interne ── -->
        <div
             class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHdrCls">
            <h2 :class="sectionTitleCls">Personnel interne <span class="text-outline/50">({{ estimation.personnels.length }})</span></h2>
            <div v-if="can_update" class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
              <a :href="edit_personnel_url" class="text-primary hover:brightness-125 transition-colors">Modifier</a>
              <span class="text-outline/30">|</span>
              <a :href="edit_personnel_detail_url" class="text-primary hover:brightness-125 transition-colors">Modifier en détail</a>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th :class="thCls + ' w-24'">Code</th>
                  <th :class="thCls">Nom</th>
                  <th v-if="show_prenom" :class="thCls">Prénom</th>
                  <th v-if="show_type_fonction" :class="thCls">Fonction</th>
                  <th v-if="can_see_costs" :class="thCls + ' w-28'">Taux/h</th>
                  <th :class="thCls + ' w-24'"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="p in estimation.personnels" :key="p.code" class="hover:bg-surface-container-highest/10">
                  <td :class="tdCls + ' font-mono text-xs'">{{ p.code }}</td>
                  <td :class="tdCls">{{ p.nom }}</td>
                  <td v-if="show_prenom" :class="tdCls">{{ p.prenom }}</td>
                  <td v-if="show_type_fonction" :class="tdCls">{{ p.type_fonction || '—' }}</td>
                  <td v-if="can_see_costs" :class="tdCls">{{ fmt(p.taux_horaire) }}</td>
                  <td :class="tdCls">
                    <a :href="p.rapport_url"
                       class="text-xs font-semibold text-primary hover:brightness-125 transition-colors">Rapports</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 5. Personnel externe ── -->
        <div
             class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHdrCls">
            <h2 :class="sectionTitleCls">Personnel externe <span class="text-outline/50">({{ estimation.personnel_externes.length }})</span></h2>
            <div v-if="can_update" class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
              <a :href="edit_personnel_externes_url" class="text-primary hover:brightness-125 transition-colors">Modifier</a>
              <span v-if="can_import_personnel_externes" class="text-outline/30">|</span>
              <a v-if="can_import_personnel_externes" :href="import_personnel_externes_url" class="text-primary hover:brightness-125 transition-colors">Importer</a>
              <span class="text-outline/30">|</span>
              <a :href="liste_personnel_externes_url" class="text-primary hover:brightness-125 transition-colors">Liste</a>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th :class="thCls + ' w-24'">Code</th>
                  <th :class="thCls">Nom</th>
                  <th v-if="show_prenom" :class="thCls">Prénom</th>
                  <th v-if="show_type_fonction" :class="thCls">Fonction</th>
                  <th v-if="can_see_costs" :class="thCls + ' w-28'">Taux/h</th>
                  <th :class="thCls + ' w-24'"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="p in estimation.personnel_externes" :key="p.code" class="hover:bg-surface-container-highest/10">
                  <td :class="tdCls + ' font-mono text-xs'">{{ p.code }}</td>
                  <td :class="tdCls">{{ p.nom }}</td>
                  <td v-if="show_prenom" :class="tdCls">{{ p.prenom }}</td>
                  <td v-if="show_type_fonction" :class="tdCls">{{ p.type_fonction || '—' }}</td>
                  <td v-if="can_see_costs" :class="tdCls">{{ fmt(p.taux_horaire) }}</td>
                  <td :class="tdCls">
                    <a :href="p.rapport_url"
                       class="text-xs font-semibold text-primary hover:brightness-125 transition-colors">Rapports</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 6. Équipements internes ── -->
        <div
             class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHdrCls">
            <h2 :class="sectionTitleCls">Équipements internes <span class="text-outline/50">({{ estimation.equipements.length }})</span></h2>
            <div v-if="can_update" class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
              <a :href="edit_equipements_url" class="text-primary hover:brightness-125 transition-colors">Modifier</a>
              <span class="text-outline/30">|</span>
              <a :href="edit_equipements_detail_url" class="text-primary hover:brightness-125 transition-colors">Modifier en détail</a>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th :class="thCls + ' w-24'">Code</th>
                  <th :class="thCls">Nom</th>
                  <th v-if="show_type_equipement" :class="thCls">Type</th>
                  <th v-if="can_see_costs" :class="thCls + ' w-28'">Taux/h</th>
                  <th :class="thCls + ' w-24'"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="e in estimation.equipements" :key="e.code" class="hover:bg-surface-container-highest/10">
                  <td :class="tdCls + ' font-mono text-xs'">{{ e.code }}</td>
                  <td :class="tdCls">{{ e.nom }}</td>
                  <td v-if="show_type_equipement" :class="tdCls">{{ e.type_equipement || '—' }}</td>
                  <td v-if="can_see_costs" :class="tdCls">{{ fmt(e.taux_horaire) }}</td>
                  <td :class="tdCls">
                    <a :href="e.rapport_url"
                       class="text-xs font-semibold text-primary hover:brightness-125 transition-colors">Rapports</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 7. Équipements externes ── -->
        <div
             class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHdrCls">
            <h2 :class="sectionTitleCls">Équipements externes <span class="text-outline/50">({{ estimation.equipement_externes.length }})</span></h2>
            <div v-if="can_update" class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
              <a :href="edit_equipement_externes_url" class="text-primary hover:brightness-125 transition-colors">Modifier</a>
              <span v-if="can_import_equipement_externes" class="text-outline/30">|</span>
              <a v-if="can_import_equipement_externes" :href="import_equipement_externes_url" class="text-primary hover:brightness-125 transition-colors">Importer</a>
              <span class="text-outline/30">|</span>
              <a :href="liste_equipement_externes_url" class="text-primary hover:brightness-125 transition-colors">Liste</a>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th :class="thCls + ' w-24'">Code</th>
                  <th :class="thCls">Nom</th>
                  <th v-if="show_type_equipement" :class="thCls">Type</th>
                  <th v-if="can_see_costs" :class="thCls + ' w-28'">Taux/h</th>
                  <th :class="thCls + ' w-24'"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="e in estimation.equipement_externes" :key="e.code" class="hover:bg-surface-container-highest/10">
                  <td :class="tdCls + ' font-mono text-xs'">{{ e.code }}</td>
                  <td :class="tdCls">{{ e.nom }}</td>
                  <td v-if="show_type_equipement" :class="tdCls">{{ e.type_equipement || '—' }}</td>
                  <td v-if="can_see_costs" :class="tdCls">{{ fmt(e.taux_horaire) }}</td>
                  <td :class="tdCls">
                    <a :href="e.rapport_url"
                       class="text-xs font-semibold text-primary hover:brightness-125 transition-colors">Rapports</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 8. Inventaires ── -->
        <div v-if="inventaire_enabled && estimation.inventaires?.length > 0"
             class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHdrCls">
            <h2 :class="sectionTitleCls">Inventaires <span class="text-outline/50">({{ estimation.inventaires.length }})</span></h2>
            <div v-if="can_update" class="flex items-center gap-2">
              <a :href="edit_inventaires_url" class="text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-colors">Modifier</a>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th :class="thCls + ' w-24'">Code</th>
                  <th :class="thCls">Nom</th>
                  <th v-if="can_see_costs" :class="thCls + ' w-28'">Taux</th>
                  <th v-if="can_see_costs && show_type_taux" :class="thCls + ' w-28'">Type taux</th>
                  <th :class="thCls + ' w-24'"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="inv in estimation.inventaires" :key="inv.code" class="hover:bg-surface-container-highest/10">
                  <td :class="tdCls + ' font-mono text-xs'">{{ inv.code }}</td>
                  <td :class="tdCls">{{ inv.nom }}</td>
                  <td v-if="can_see_costs" :class="tdCls">{{ fmt(inv.taux) }}</td>
                  <td v-if="can_see_costs && show_type_taux" :class="tdCls">{{ inv.type_taux || '—' }}</td>
                  <td :class="tdCls">
                    <a v-if="inv.dependant" :href="inv.rapport_url"
                       class="text-xs font-semibold text-primary hover:brightness-125 transition-colors">Rapports</a>
                    <a v-else-if="inv.independant && can_delete" href="#"
                       class="text-xs font-semibold text-error hover:brightness-125 transition-colors"
                       @click.prevent="deleteInventaire(inv)">Supprimer</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 9. Items ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHdrCls">
            <h2 :class="sectionTitleCls">
              Items <span class="text-outline/50">({{ estimation.elements?.length ?? 0 }})</span>
            </h2>
            <div v-if="can_update" class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
              <a :href="edit_items_url" class="text-primary hover:brightness-125 transition-colors">Modifier</a>
              <span v-if="can_import_items" class="text-outline/30">|</span>
              <a v-if="can_import_items" :href="import_items_url" class="text-primary hover:brightness-125 transition-colors">Importer</a>
            </div>
          </div>
          <div v-if="!estimation.elements?.length" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">list_alt</span>
            Aucun item
          </div>
          <div v-else>
            <div v-for="group in elementGroups" :key="group.tag">
              <div v-if="group.tag" class="px-6 py-2 bg-surface-container-high/60 border-y border-outline-variant/10 flex items-center gap-2">
                <span class="text-[10px] font-black uppercase tracking-widest text-primary">{{ group.tag }}</span>
                <span class="text-[10px] text-outline/50">({{ group.elements.length }})</span>
              </div>
              <div class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls + ' w-24'">Code</th>
                      <th :class="thCls">Description</th>
                      <th :class="thCls + ' w-20'">Qté</th>
                      <th :class="thCls + ' w-16'">Unité</th>
                      <template v-if="can_see_costs">
                        <th v-for="tp in type_parties" :key="tp.id" :class="thCls + ' w-28'">{{ tp.code }}</th>
                        <th :class="thCls + ' w-28'">Total</th>
                      </template>
                      <th :class="thCls + ' w-24'"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="el in group.elements" :key="el.code" class="hover:bg-surface-container-highest/10">
                      <td :class="tdCls + ' font-mono text-xs'">{{ el.code }}</td>
                      <td :class="tdCls">{{ el.description }}</td>
                      <td :class="tdCls">{{ el.quantite }}</td>
                      <td :class="tdCls">{{ el.type_unite || '—' }}</td>
                      <template v-if="can_see_costs">
                        <td v-for="(val, j) in el.parties" :key="j" :class="tdCls + ' text-right font-mono text-xs'">{{ fmt(val) }}</td>
                        <td :class="tdCls + ' text-right font-mono text-xs font-bold'">{{ fmt(el.total) }}</td>
                      </template>
                      <td :class="tdCls">
                        <a v-if="el.dependant" :href="el.rapport_url"
                           class="text-xs font-semibold text-primary hover:brightness-125 transition-colors">Rapports</a>
                        <a v-else-if="el.independant && can_delete" :href="el.destroy_url" data-method="delete"
                           class="text-xs font-semibold text-error hover:brightness-125 transition-colors"
                           @click.prevent="deleteElement(el)">Supprimer</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="pb-8"></div>
      </div>
    </div>

    <!-- Bouton flottant ↑ -->
    <div class="fixed bottom-[39px] right-6 z-50">
      <Transition
        enter-active-class="transition-all duration-200"
        enter-from-class="opacity-0 translate-y-3 scale-90"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition-all duration-150"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-3 scale-90">
        <button v-if="showScrollTop" type="button" @click="scrollToTop"
                class="w-10 h-10 rounded-full bg-surface-container border border-outline-variant/20 shadow-lg flex items-center justify-center text-outline hover:text-on-surface hover:bg-surface-container-highest active:scale-95 transition-all">
          <span class="material-symbols-outlined text-base">arrow_upward</span>
        </button>
      </Transition>
    </div>

  </AppLayout>
</template>
